import { MdGroup, MdSwapHoriz } from 'react-icons/md';
import { IoCardSharp } from 'react-icons/io5';
import Widget from 'components/widget/Widget';
import Card from 'components/card';
import useSWR from 'swr';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { useState, useEffect } from 'react';
import { useWindowSize } from 'usehooks-ts';
import { MdAttachMoney } from 'react-icons/md';
import { balanceService } from 'views/services/balanceService';
import { usersService } from 'views/services/usersService';
import { formatNumber } from 'utils/formatNumber';
import { txsService } from 'views/services/txsService';
import { cardsService } from '../../services/cardsService';
import { ReadPermissionGate } from 'components/PermissionGate';
import { PERMISSION_RESOURCES } from 'constants/permissions';

const Dashboard = () => {
  const { data: statistics, isLoading } = useSWR(
    '/users-statistics',
    () => usersService.getUsersStatistic(),
    { refreshInterval: 10000 }
  );
  const { data: ordersStatistics, isLoading: isLoadingOrdersStatistics } =
    useSWR('/orders/statistics', () => txsService.getStatistics(), {
      refreshInterval: 10000,
    });
  const { width } = useWindowSize();

  const { data: balancesData, isLoading: isLoadingAdminBalanses } = useSWR(
    `/api/admin/balances`,
    () => balanceService.getAdminBalances(),
    {
      refreshInterval: 5000,
    }
  );

  const { data: cardsStatistics, isLoading: isLoadingCardsStatistics } = useSWR(
    '/cards/statistics',
    () => cardsService.getStatistics(),
    {
      refreshInterval: 60000,
    }
  );

  const [chartWidth, setChartWidth] = useState(250);

  useEffect(() => {
    const updateChartSize = () => {
      setChartWidth(width < 450 ? 140 : 220);
    };
    updateChartSize();
    window.addEventListener('resize', updateChartSize);
    return () => window.removeEventListener('resize', updateChartSize);
  }, [width]);

  const series = Object.values(balancesData?.platformBalances ?? {}).map(
    item => item.amountInUsdt
  );

  const hasData = series.length > 0 && series.some(value => value > 0);
  const chartOptions: ApexOptions = {
    chart: {
      type: 'donut',
    },
    labels: hasData
      ? Object.keys(balancesData?.platformBalances ?? {})
      : ['No data'],
    legend: {
      show: false,
    },
    dataLabels: {
      enabled: false,
    },
    tooltip: {
      y: {
        formatter: value => (hasData ? formatNumber(value).toString() : '0'),
      },
    },
  };

  const chartSeries = hasData ? series : [1];
  const chartLabels = hasData
    ? Object.keys(balancesData?.platformBalances ?? {})
    : ['No data'];

  return (
    <div>
      <div className="mt-5 md-max:flex-col md-max:items-center flex w-full gap-5">
        <div className="flex flex-col gap-3 w-full max-w-[535px]">
          <ReadPermissionGate resource={PERMISSION_RESOURCES.TOTAL_USERS}>
            <Widget
              icon={<MdGroup className="h-7 w-7" />}
              title={'Total users'}
              subtitle={
                isLoading
                  ? 'Loading...'
                  : statistics?.totalUsersCount?.toString()
              }
            />
          </ReadPermissionGate>

          <ReadPermissionGate
            resource={PERMISSION_RESOURCES.TOTAL_CARD_BALANCE}
          >
            <Widget
              icon={<MdAttachMoney className="h-7 w-7" />}
              title={'Total card balance'}
              subtitle={
                isLoadingCardsStatistics
                  ? 'Loading...'
                  : cardsStatistics?.totalCardBalances || 0
              }
            />
          </ReadPermissionGate>

          <ReadPermissionGate
            resource={PERMISSION_RESOURCES.TOTAL_ISSUED_CARDS}
          >
            <Widget
              icon={<IoCardSharp className="h-7 w-7" />}
              title={'Total issued cards'}
              subtitle={
                isLoadingCardsStatistics
                  ? 'Loading...'
                  : cardsStatistics?.totalCardIssued || 0
              }
            />
          </ReadPermissionGate>

          <ReadPermissionGate
            resource={PERMISSION_RESOURCES.TOTAL_ACTIVE_CARDS}
          >
            <Widget
              icon={<IoCardSharp className="h-7 w-7" />}
              title={'Total active cards'}
              subtitle={
                isLoadingCardsStatistics
                  ? 'Loading...'
                  : cardsStatistics?.totalActiveCards || 0
              }
            />
          </ReadPermissionGate>
        </div>
        <ReadPermissionGate
          resource={PERMISSION_RESOURCES.TOTAL_CLIENTS_WALLET_BALANCE}
        >
          <div className="lg-max:mt-2 w-full max-w-[535px]">
            <Card>
              <div className="h-full p-5 flex flex-col justify-center">
                <h1 className="text-[20px] font-bold">
                  Total Clients Wallet Balance:{' '}
                  {
                    <>
                      {isLoadingAdminBalanses ? (
                        'Loading...'
                      ) : (
                        <>{formatNumber(balancesData?.totalInUsdt) ?? 0} $</>
                      )}
                    </>
                  }
                </h1>

                <div className="flex justify-between gap-4 items-center">
                  <ul className="mt-3">
                    {Object.entries(balancesData?.platformBalances ?? {}).map(
                      ([currency, { amount, amountInUsdt }]) => (
                        <li
                          key={currency}
                          className="flex items-center justify-between lg-max:w-[100px] w-[120px]"
                        >
                          <span className="lg-max:text-[12px] text-[17px]">
                            {currency}:
                          </span>
                          <div>
                            <span className="ml-3">{formatNumber(amount)}</span>
                            <span className="ml-2 lg-max:text-[12px] text-[14px] text-[grey]">
                              ({formatNumber(amountInUsdt)}
                              <span className="ml-1">USDT</span>)
                            </span>
                          </div>
                        </li>
                      )
                    )}
                  </ul>
                  <Chart
                    options={{ ...chartOptions, labels: chartLabels }}
                    series={chartSeries}
                    type="donut"
                    width={chartWidth}
                  />
                </div>
              </div>
            </Card>
          </div>
        </ReadPermissionGate>
      </div>

      <div className="mt-5 max-w-[1090px] flex flex-col w-full gap-1">
        <ReadPermissionGate resource={PERMISSION_RESOURCES.TOTAL_TRANSACTIONS}>
          <Widget
            icon={<MdAttachMoney className="h-7 w-7" />}
            title={'Total transactions'}
            subtitle={
              isLoadingOrdersStatistics
                ? 'Loading...'
                : ordersStatistics?.total.toString()
            }
          />
        </ReadPermissionGate>

        <ReadPermissionGate resource={PERMISSION_RESOURCES.TOTAL_VOLUME}>
          <Widget
            icon={<MdAttachMoney className="h-7 w-7" />}
            title={'Total Volume'}
            subtitle={
              isLoadingOrdersStatistics
                ? 'Loading...'
                : formatNumber(ordersStatistics?.totalVolume ?? 0)
            }
          />
        </ReadPermissionGate>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 mt-1 gap-1 max-w-[1090px]">
        <ReadPermissionGate
          resource={PERMISSION_RESOURCES.DEPOSIT_TRANSACTIONS}
        >
          <Widget
            icon={<MdAttachMoney className="h-7 w-7" />}
            title={'Deposit transactions'}
            subtitle={
              isLoadingOrdersStatistics
                ? 'Loading...'
                : ordersStatistics?.depositOrders.toString()
            }
          />
        </ReadPermissionGate>

        <ReadPermissionGate
          resource={PERMISSION_RESOURCES.INTERNAL_SEND_TRANSACTIONS}
        >
          <Widget
            icon={<MdAttachMoney className="h-7 w-7" />}
            title={'Internal (send) transactions'}
            subtitle={
              isLoadingOrdersStatistics
                ? 'Loading...'
                : ordersStatistics?.internalOrders.toString()
            }
          />
        </ReadPermissionGate>

        <ReadPermissionGate
          resource={PERMISSION_RESOURCES.CRYPTO_TRANSFERS_WITHDRAWALS}
        >
          <Widget
            icon={<MdAttachMoney className="h-7 w-7" />}
            title={'Crypto transfers (withdraws)'}
            subtitle={
              isLoadingOrdersStatistics
                ? 'Loading...'
                : ordersStatistics?.withdrawOrders.toString()
            }
          />
        </ReadPermissionGate>

        <ReadPermissionGate resource={PERMISSION_RESOURCES.CARD_ISSUE_ATTEMPTS}>
          <Widget
            icon={<IoCardSharp className="h-7 w-7" />}
            title={'Card issue attempts transactions'}
            subtitle={
              isLoadingOrdersStatistics
                ? 'Loading...'
                : ordersStatistics?.cardsIssueOrders.toString()
            }
          />
        </ReadPermissionGate>

        <ReadPermissionGate
          resource={PERMISSION_RESOURCES.CARD_TOP_UP_TRANSACTIONS}
        >
          <Widget
            icon={<IoCardSharp className="h-7 w-7" />}
            title={'Card top-up transactions'}
            subtitle={
              isLoadingOrdersStatistics
                ? 'Loading...'
                : ordersStatistics?.cardTopUpsOrders.toString()
            }
          />
        </ReadPermissionGate>

        <ReadPermissionGate resource={PERMISSION_RESOURCES.SWAP_TRANSACTIONS}>
          <Widget
            icon={<MdSwapHoriz className="h-7 w-7" />}
            title={'Swap transactions'}
            subtitle={
              isLoadingOrdersStatistics
                ? 'Loading...'
                : ordersStatistics?.swapOrders.toString()
            }
          />
        </ReadPermissionGate>

        <ReadPermissionGate resource={PERMISSION_RESOURCES.REFERRAL_COUNT}>
          <Widget
            icon={<MdGroup className="h-7 w-7" />}
            title={'Referral Count'}
            subtitle={
              isLoading ? 'Loading...' : (statistics?.totalReferralsCount ?? 0)
            }
          />
        </ReadPermissionGate>
      </div>
    </div>
  );
};

export default Dashboard;
